import React, { useState, useEffect } from "react";
import {
  upsertDOBProfile,
  searchDOBProfileByEmail,
} from "../../services/profileService";
import DOBEmailLookup from "./DOBEmailLookup";
import { useHeaders } from "../../HeadersContext";

const DOBProfile = ({
  onProfileConfirmed,
  sectionName,
  profiles: propProfiles,
}) => {
  const { headers } = useHeaders();
  const [profiles, setProfiles] = useState(propProfiles || []);
  const [selectedProfile, setSelectedProfile] = useState(null);
  const [selectedLicense, setSelectedLicense] = useState("");
  const [showEmailLookup, setShowEmailLookup] = useState(false);
  const [isUpdating, setIsUpdating] = useState(false);
  // When true, the profile is confirmed permanently until reset.
  const [profileConfirmed, setProfileConfirmed] = useState(false);

  const formatDate = (dateString) => {
    if (!dateString) return "N/A";
    return new Date(dateString).toLocaleString("en-US");
  };

  // Update local profiles state when propProfiles changes.
  useEffect(() => {
    setProfiles(propProfiles || []);
  }, [propProfiles]);

  // Fallback: Only fetch profiles if no profiles prop is provided.
  const fetchProfiles = async () => {
    if (!propProfiles) {
      const { fetchAllDOBProfiles } = await import(
        "../../services/profileService"
      );
      const profileData = await fetchAllDOBProfiles();
      setProfiles(profileData || []);
    }
  };

  const handleProfileChange = (e) => {
    const profileId = e.target.value;
    const profile = profileId
      ? profiles.find((p) => String(p.id) === profileId)
      : null;
    setSelectedProfile(profile);
    setSelectedLicense("");
    setShowEmailLookup(false);
  };

  const handleLicenseChange = (e) => {
    setSelectedLicense(e.target.value);
  };

  const handleConfirmProfile = () => {
    if (selectedProfile) {
      let updatedProfile = { ...selectedProfile };
      if (
        updatedProfile.licenseList &&
        updatedProfile.licenseList.length > 0 &&
        selectedLicense
      ) {
        const chosenLicense = updatedProfile.licenseList.find(
          (lic) => String(lic.id) === selectedLicense
        );
        if (chosenLicense) {
          // Remove the id fields, similar to your existing logic.
          const { id, ownerLookupId, ...licenseWithoutIds } = chosenLicense;
          updatedProfile.licenseList = [licenseWithoutIds];
        }
      }
      // Omit extra fields before confirming.
      const { id, lastUpdated, ...trimmedProfile } = updatedProfile;
      console.log("Confirmed DOB Profile:", trimmedProfile);
      if (onProfileConfirmed) {
        onProfileConfirmed(trimmedProfile);
      }
      // Permanently display the confirmation UI.
      setProfileConfirmed(true);
    }
  };

  // Reset to full form view so the user can select a new profile.
  const handleResetProfile = () => {
    setSelectedProfile(null);
    setSelectedLicense("");
    setProfileConfirmed(false);
    setShowEmailLookup(false);
  };

  const handleUpdateDirector = async () => {
    if (selectedProfile) {
      setIsUpdating(true);
      try {
        const lookupObject = { Email: selectedProfile.email };
        const result = await searchDOBProfileByEmail(lookupObject, headers);
        let freshProfile = result;
        if (
          result.OwnerInformation &&
          Array.isArray(result.OwnerInformation) &&
          result.OwnerInformation.length > 0
        ) {
          const info = result.OwnerInformation[0];
          freshProfile = {
            contactGuid: info.ContactGuid,
            businessName: info.BusinessName,
            businessPhone: info.BusinessPhone,
            city: info.City,
            email: info.Email,
            firstName: info.FirstName,
            lastName: info.LastName,
            licenseList: info.LicenseList || [],
            middleName: info.MiddleName,
            mobile: info.Mobile,
            ownerType: info.OwnerType,
            state: info.State,
            street: info.Street,
            zip: info.Zip,
          };
        }
        const updatedDirector = await upsertDOBProfile(freshProfile);
        setSelectedProfile(updatedDirector);
        setProfiles((prev) =>
          prev.map((dir) =>
            dir.id === updatedDirector.id ? updatedDirector : dir
          )
        );
      } catch (error) {
        console.error("Error updating director", error);
      }
      setIsUpdating(false);
    }
  };

  const handleShowEmailLookup = () => {
    setShowEmailLookup(true);
  };

  useEffect(() => {
    if (!propProfiles) {
      fetchProfiles();
    }
  }, [propProfiles]);

  // If the profile is confirmed, show the simplified view.
  if (profileConfirmed && selectedProfile) {
    return (
      <div className="relative border border-gray-300 rounded-md p-4 max-w-[400px]">
        <div className="mb-4">
          <h2 className="text-lg font-bold text-green-600">
            Profile Confirmed!
          </h2>
        </div>
        <div className="mb-2">
          <span className="font-bold">Name: </span>
          {selectedProfile.firstName} {selectedProfile.lastName}{" "}
          {selectedProfile.businessName
            ? `(${selectedProfile.businessName})`
            : ""}
        </div>
        <div className="mb-2">
          <span className="font-bold">Email: </span>
          {selectedProfile.email}
        </div>
        {selectedProfile.licenseList &&
          selectedProfile.licenseList.length > 0 &&
          selectedLicense && (
            <div className="mb-2">
              <span className="font-bold">Selected License: </span>
              {
                selectedProfile.licenseList.find(
                  (lic) => String(lic.id) === selectedLicense
                )?.LicenseNumber
              }{" "}
              (
              {
                selectedProfile.licenseList.find(
                  (lic) => String(lic.id) === selectedLicense
                )?.LicenseDescription
              }
              )
            </div>
          )}
        <button
          onClick={handleResetProfile}
          className="bg-blue-500 text-white px-3 py-1 rounded mt-4"
        >
          Select New Profile
        </button>
      </div>
    );
  }

  // Full form view.
  return (
    <div className="relative border border-gray-300 rounded-md p-4 max-w-[400px]">
      <div className="mb-4">
        <button
          className="my-2 bg-blue-500 text-white px-3 py-1 rounded"
          onClick={handleShowEmailLookup}
        >
          Search New Profile
        </button>
        <select
          onChange={handleProfileChange}
          value={selectedProfile ? String(selectedProfile.id) : ""}
          className="w-full p-2 border border-gray-300 rounded"
        >
          <option value="">Select a Profile</option>
          {(profiles || []).map((director) => (
            <option key={director.id} value={director.id}>
              {director.email}
            </option>
          ))}
        </select>
      </div>

      <div className="mb-4">
        {selectedProfile ? (
          <>
            <div className="mb-2">
              <label className="font-bold mr-2">Name:</label>
              <span>
                {selectedProfile.firstName || ""}{" "}
                {selectedProfile.lastName || ""}
                {selectedProfile.businessName
                  ? ` (${selectedProfile.businessName})`
                  : ""}
              </span>
            </div>
            <div className="mb-2">
              <label className="font-bold mr-2">Email:</label>
              <span>{selectedProfile.email}</span>
            </div>
            <div className="mb-2 flex items-center">
              <label className="font-bold mr-2">Last Updated:</label>
              <span>{formatDate(selectedProfile.lastUpdated)}</span>
              <button
                onClick={handleUpdateDirector}
                className="ml-2 bg-blue-500 text-white px-2 py-1 rounded"
                disabled={isUpdating}
              >
                {isUpdating ? "Updating..." : "Update"}
              </button>
            </div>
            {selectedProfile.licenseList &&
              selectedProfile.licenseList.length > 0 && (
                <div className="mb-2">
                  <label className="font-bold mr-2">Select License:</label>
                  <select
                    onChange={handleLicenseChange}
                    value={selectedLicense}
                    className="p-2 border border-gray-300 rounded"
                  >
                    <option value="">Select a License</option>
                    {selectedProfile.licenseList.map((license) => (
                      <option key={license.id} value={license.id}>
                        {license.LicenseNumber} ({license.LicenseDescription})
                      </option>
                    ))}
                  </select>
                </div>
              )}
            <button
              onClick={handleConfirmProfile}
              className={`bg-green-500 text-white px-3 py-1 rounded ${
                !selectedProfile ||
                (selectedProfile.licenseList &&
                  selectedProfile.licenseList.length > 0 &&
                  !selectedLicense)
                  ? "opacity-50 cursor-not-allowed"
                  : ""
              }`}
              disabled={
                !selectedProfile ||
                (selectedProfile.licenseList &&
                  selectedProfile.licenseList.length > 0 &&
                  !selectedLicense)
              }
            >
              Confirm
            </button>
          </>
        ) : (
          <div className="text-gray-500">No Profile selected</div>
        )}

        {showEmailLookup && (
          <DOBEmailLookup onCancel={() => setShowEmailLookup(false)} />
        )}
      </div>
    </div>
  );
};

export default DOBProfile;
